// @ts-nocheck
/**
 * fileTableManifest — parse/serialize the SQLite-stored URI manifest for fileTable properties.
 */

import type { FileTableManifest } from '../../types/category';

export const FILE_TABLE_MANIFEST_KIND = 'fileTable' as const;

export const DEFAULT_MAX_FILE_TABLE_PART_BYTES = 512 * 1024;

export function isFileTableManifest(value: unknown): value is FileTableManifest {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (record.kind !== FILE_TABLE_MANIFEST_KIND) {
    return false;
  }
  if (!Array.isArray(record.parts)) {
    return false;
  }
  if (!record.parts.every((part) => typeof part === 'string' && part.length > 0)) {
    return false;
  }
  if (record.revision !== undefined) {
    if (typeof record.revision !== 'number' || !Number.isFinite(record.revision) || record.revision < 0) {
      return false;
    }
  }
  return true;
}

export function parseFileTableManifest(raw: string | undefined | null): FileTableManifest | null {
  if (raw == null || typeof raw !== 'string') {
    return null;
  }
  const trimmed = raw.trim();
  if (!trimmed || !trimmed.startsWith('{')) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return isFileTableManifest(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function nextFileTableRevision(previous: FileTableManifest | null | undefined): number {
  return (previous?.revision ?? 0) + 1;
}

/** Build a manifest with bumped revision for a write that may keep the same part paths. */
export function fileTableManifestWithRevision(
  parts: readonly string[],
  previous: FileTableManifest | null | undefined,
): FileTableManifest {
  return {
    kind: FILE_TABLE_MANIFEST_KIND,
    parts: [...parts],
    revision: nextFileTableRevision(previous),
  };
}

export function serializeFileTableManifest(manifest: FileTableManifest): string {
  const payload: FileTableManifest = {
    kind: FILE_TABLE_MANIFEST_KIND,
    parts: [...manifest.parts],
  };
  if (manifest.revision != null) {
    payload.revision = manifest.revision;
  }
  return JSON.stringify(payload);
}

export function emptyFileTableManifest(): FileTableManifest {
  return { kind: FILE_TABLE_MANIFEST_KIND, parts: [] };
}

/** True when the stored property value looks like a fileTable manifest (not CTBL base64). */
export function looksLikeFileTableManifestValue(raw: string | undefined | null): boolean {
  return parseFileTableManifest(raw) != null;
}
